import axios from "axios";
import env from "../env";
import customConfig from "../config";
import { toUiTemplate } from "../components/notificationManagement/notificationFormUtils";

const TEMPLATES_PATH = "/simplifi/v1/notificationmanager/templates";
const jsonConfig = {
  headers: {
    "Content-Type": "application/json",
  },
};

const templatesUrl = () =>
  `${env.GET_DEVICEMANAGEMENT_API_URL()}${TEMPLATES_PATH}`;

const templateByIdUrl = (templateId) =>
  `${templatesUrl()}/${encodeURIComponent(templateId)}`;

const getErrorMessage = (error, fallback) => {
  if (error?.userMessage) return error.userMessage;
  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  if (typeof data?.error?.message === "string" && data.error.message.trim()) {
    return data.error.message;
  }
  if (error?.request) return customConfig.NETWORK_ERROR;
  return error?.message || fallback || customConfig.SOMETHING_WENT_WRONG;
};

const compactParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

const unwrapPayload = (data, depth = 0) => {
  if (data == null || depth > 3) return data;
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return data;
  if (
    Array.isArray(data.templates) ||
    data.templateId ||
    data.name ||
    data.templateName ||
    data.content
  ) {
    return data;
  }
  if (data.data !== undefined) return unwrapPayload(data.data, depth + 1);
  if (data.payload !== undefined) return unwrapPayload(data.payload, depth + 1);
  if (data.result !== undefined) return unwrapPayload(data.result, depth + 1);
  return data;
};

const parseTemplateList = (data) => {
  const payload = unwrapPayload(data);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.templateId || payload?.name || payload?.templateName) return [payload];
  return [];
};

const parseTemplate = (data, templateId) => {
  const list = parseTemplateList(data);
  if (templateId && list.length) {
    return list.find((item) => (item.templateId || item.id) === templateId) || null;
  }
  if (list.length === 1) return list[0];
  const payload = unwrapPayload(data);
  if (payload && !Array.isArray(payload) && (payload.templateId || payload.name || payload.content)) {
    return payload;
  }
  return null;
};

const mapTemplateResponse = (data, fallback = {}) => {
  const parsed = parseTemplate(data) || fallback;
  return toUiTemplate({
    ...fallback,
    ...(parsed && typeof parsed === "object" ? parsed : {}),
  });
};

export const notificationTemplateService = {
  async getTemplates(filters = {}) {
    try {
      const params = compactParams({
        channel: filters.channel || filters.notificationType,
        templateId: filters.templateId,
        type: filters.type,
        category: filters.category,
        pageIndex: filters.pageIndex,
        pageSize: filters.pageSize,
      });
      const response = await axios.get(templatesUrl(), { ...jsonConfig, params });
      const templates = parseTemplateList(response?.data).map(toUiTemplate);
      return { success: true, data: templates };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: getErrorMessage(error, customConfig.FETCH_FAILURE),
      };
    }
  },

  async getTemplateById(templateId) {
    if (!templateId) {
      return { success: false, data: null, message: "Template not found" };
    }
    try {
      const response = await axios.get(templatesUrl(), {
        ...jsonConfig,
        params: compactParams({ templateId }),
      });
      const template = parseTemplate(response?.data, templateId);
      if (!template) {
        return { success: false, data: null, message: "Template not found" };
      }
      return { success: true, data: toUiTemplate(template) };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: getErrorMessage(error, customConfig.FETCH_FAILURE),
      };
    }
  },

  async createTemplate(payload) {
    try {
      const response = await axios.post(templatesUrl(), payload, jsonConfig);
      return {
        success: true,
        data: mapTemplateResponse(response?.data, payload),
        message: "Template created successfully.",
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: getErrorMessage(error, customConfig.ADD_FAILURE),
      };
    }
  },

  async updateTemplate(templateId, payload) {
    try {
      const response = await axios.put(
        templateByIdUrl(templateId),
        payload,
        jsonConfig
      );
      return {
        success: true,
        data: mapTemplateResponse(response?.data, { ...payload, templateId }),
        message: "Template updated successfully.",
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: getErrorMessage(error, customConfig.UPDATE_FAILURE),
      };
    }
  },

  async deleteTemplate(templateId) {
    try {
      const response = await axios.delete(templateByIdUrl(templateId), jsonConfig);
      return {
        success: true,
        data: { id: templateId, templateId },
        message: response?.data?.message || "Template deleted successfully",
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: getErrorMessage(error, customConfig.DELETE_FAILURE),
      };
    }
  },
};
